import { useMemo, useState, useEffect } from 'react';
import { collection, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { THREAD_FILTERS, removeBlankDuplicateThreads, shouldShowThread } from '../threadState';
import { readOfflineContentCache, writeOfflineContentCache } from '../../../utils/offlineContentCache';
import { useOnlineRefreshSignal } from '../../../utils/onlineStatus';

const CACHE_SCOPE = 'messages';

/**
 * useThreads hook
 *
 * Subscribes to all message threads visible to the current user,
 * sorts them by lastUpdated, and fetches other participants' profiles.
 *
 * @param {object|null} user - current user object from AuthContext
 * @returns {{ threads: Array, loading: boolean, userProfiles: Object, threadStates: Object }}
 */
export default function useThreads(user, filter = THREAD_FILTERS.inbox, routeContext = {}) {
  const [rawThreads, setRawThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [loadingStates, setLoadingStates] = useState(true);
  const [threadStates, setThreadStates] = useState({});
  const [userProfiles, setUserProfiles] = useState({});
  const onlineRefreshSignal = useOnlineRefreshSignal();

  const threads = useMemo(() => removeBlankDuplicateThreads(rawThreads
    .filter((thread) => shouldShowThread(thread, threadStates[thread.id], filter))
    .sort((a, b) => (b.lastUpdated?.toMillis?.() || 0) - (a.lastUpdated?.toMillis?.() || 0))), [rawThreads, threadStates, filter]);

  useEffect(() => {
    if (!user) {
      setRawThreads([]);
      setThreadStates({});
      setLoadingThreads(false);
      setThreadsLoaded(false);
      setLoadingStates(false);
      return;
    }
    setLoadingThreads(true);
    setThreadsLoaded(false);
    setRawThreads(readOfflineContentCache([user.uid, CACHE_SCOPE, 'threads'], []));
    const threadsCol = collection(db, 'messages');
    const q = query(threadsCol, where('visibleTo', 'array-contains', user.uid));
    const unsub = safeOnSnapshot(q, snap => {
      const data = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        currentUserId: user.uid
      }));
      setRawThreads(data);
      writeOfflineContentCache([user.uid, CACHE_SCOPE, 'threads'], data);
      setThreadsLoaded(true);
      setLoadingThreads(false);
    }, err => {
      setThreadsLoaded(false);
      setLoadingThreads(false);
    }, { label: 'messages.threads', uid: user.uid, ...routeContext });
    return () => unsub();
  }, [onlineRefreshSignal, routeContext, user]);

  useEffect(() => {
    if (!user?.uid) {
      setThreadStates({});
      setLoadingStates(false);
      return undefined;
    }

    setLoadingStates(true);
    setThreadStates(readOfflineContentCache([user.uid, CACHE_SCOPE, 'threadStates'], {}));
    const statesRef = collection(db, 'privateUsers', user.uid, 'messageThreadStates');
    const unsub = safeOnSnapshot(statesRef, snap => {
      const states = {};
      snap.docs.forEach((stateDoc) => {
        states[stateDoc.id] = {
          id: stateDoc.id,
          ...stateDoc.data()
        };
      });
      setThreadStates(states);
      writeOfflineContentCache([user.uid, CACHE_SCOPE, 'threadStates'], states);
      setLoadingStates(false);
    }, () => {
      setThreadStates({});
      setLoadingStates(false);
    }, { label: 'messages.threadStates', uid: user.uid, ...routeContext });

    return () => unsub();
  }, [onlineRefreshSignal, routeContext, user?.uid]);

  useEffect(() => {
    if (user?.uid) {
      setUserProfiles(readOfflineContentCache([user.uid, CACHE_SCOPE, 'userProfiles'], {}));
    } else {
      setUserProfiles({});
    }
  }, [user?.uid]);

  useEffect(() => {
    // Fetch profiles for all participants (excluding current user)
    threads.forEach(thread => {
      thread.participants.forEach(uid => {
        if (uid === user?.uid) return;
        if (userProfiles[uid]) return;
        getDoc(doc(db, 'users', uid)).then(snap => {
          if (snap.exists()) {
            const {
              firstName = '',
              lastName = ''
            } = snap.data();
            setUserProfiles(prev => {
              const nextProfiles = {
                ...prev,
                [uid]: {
                  firstName,
                  lastName
                }
              };
              if (user?.uid) {
                writeOfflineContentCache([user.uid, CACHE_SCOPE, 'userProfiles'], nextProfiles);
              }
              return nextProfiles;
            });
          }
        }).catch(err => void 0);
      });
    });
  }, [threads, userProfiles, user]);

  return {
    threads,
    allThreads: rawThreads,
    threadsLoaded,
    loading: loadingThreads || loadingStates,
    userProfiles,
    threadStates
  };
}
